import {
  Button,
  DatePicker,
  FormField,
  Input,
  MoneyInput,
  Select,
  Text,
  Textarea,
  todayIso,
  useToast,
} from '@ui';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '../../../app/FormDialog';
import { errorMessage, formErrors } from '../../../lib/errors';
import {
  useFinanceCategories,
  useReceipt,
  useSaveEntry,
  type Entry,
  type EntryKind,
  type FinanceMethod,
} from '../api';
import styles from '../pages/FinancePages.module.css';

const METHODS: FinanceMethod[] = ['cash', 'transfer', 'paypal', 'card', 'other'];

export interface EntryFormDialogProps {
  open: boolean;
  onClose: () => void;
  kind: EntryKind;
  entry?: Entry;
  onSaved?: (entry: Entry) => void;
}

/** Records or edits a manual income or expense (FIN-001, FIN-002, FIN-004). */
export function EntryFormDialog({ open, onClose, kind, entry, onSaved }: EntryFormDialogProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const save = useSaveEntry();
  const receipt = useReceipt();
  const categories = useFinanceCategories(open);
  const file = useRef<HTMLInputElement>(null);
  const [bookedOn, setBookedOn] = useState(entry?.booked_on ?? todayIso());
  const [amount, setAmount] = useState<number | null>(entry?.amount_cents ?? null);
  const [category, setCategory] = useState(entry?.category.id ?? '');
  const [description, setDescription] = useState(entry?.description ?? '');
  const [counterparty, setCounterparty] = useState(entry?.counterparty ?? '');
  const [method, setMethod] = useState<string>(entry?.method ?? '');
  const [notes, setNotes] = useState(entry?.notes ?? '');
  const [upload, setUpload] = useState<File | null>(null);
  const options = (categories.data ?? []).filter(
    (c) => c.kind === kind && (!c.archived || c.id === entry?.category.id),
  );
  const errors = formErrors(save.error, {
    amount: ['entry.amount'],
    category: ['category.'],
    description: ['entry.description'],
    date: ['entry.date'],
    counterparty: ['entry.counterparty'],
  });
  const close = () => {
    save.reset();
    onClose();
  };
  const submit = () =>
    save.mutate(
      {
        id: entry?.id,
        kind,
        version: entry?.version,
        body: {
          booked_on: bookedOn,
          amount_cents: amount ?? 0,
          category_id: category,
          description,
          counterparty: counterparty || null,
          method: (method || null) as FinanceMethod | null,
          notes: notes || null,
        },
      },
      {
        onSuccess: (saved) => {
          const done = (e: Entry) => {
            toast.show(entry ? t('finance.form.saved') : t(`finance.form.recorded.${kind}`));
            onSaved?.(e);
            close();
          };
          if (upload) {
            receipt.mutate(
              { id: saved.id, file: upload },
              {
                onSuccess: done,
                onError: (err) => {
                  toast.show(errorMessage(err), 'danger');
                  done(saved);
                },
              },
            );
          } else {
            done(saved);
          }
        },
      },
    );

  return (
    <FormDialog
      open={open}
      onClose={close}
      drawer
      title={entry ? t(`finance.form.editTitle.${kind}`) : t(`finance.form.newTitle.${kind}`)}
      submitLabel={t('common.save')}
      onSubmit={submit}
      pending={save.isPending || receipt.isPending}
      error={errors.general}
    >
      <div className={styles.pair}>
        <FormField label={t('finance.form.amount')} required error={errors.field.amount}>
          {(p) => (
            <MoneyInput
              {...p}
              value={amount}
              onChange={setAmount}
              labels={{ decrement: t('common.decrease'), increment: t('common.increase') }}
            />
          )}
        </FormField>
        <FormField label={t('finance.form.date')} required error={errors.field.date}>
          {(p) => <DatePicker {...p} value={bookedOn} onChange={setBookedOn} />}
        </FormField>
      </div>
      <FormField label={t('finance.form.category')} required error={errors.field.category}>
        {(p) => (
          <Select
            {...p}
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            options={[
              { value: '', label: t('finance.form.chooseCategory') },
              ...options.map((c) => ({ value: c.id, label: c.name })),
            ]}
          />
        )}
      </FormField>
      <FormField label={t('finance.form.description')} required error={errors.field.description}>
        {(p) => (
          <Input
            {...p}
            value={description}
            maxLength={200}
            onChange={(e) => setDescription(e.target.value)}
          />
        )}
      </FormField>
      <div className={styles.pair}>
        <FormField label={t(`finance.form.counterparty.${kind}`)} error={errors.field.counterparty}>
          {(p) => (
            <Input {...p} value={counterparty} onChange={(e) => setCounterparty(e.target.value)} />
          )}
        </FormField>
        <FormField label={t('finance.form.method')}>
          {(p) => (
            <Select
              {...p}
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              options={[
                { value: '', label: t('finance.method.none') },
                ...METHODS.map((m) => ({ value: m, label: t(`finance.method.${m}`) })),
              ]}
            />
          )}
        </FormField>
      </div>
      <FormField label={t('finance.form.notes')}>
        {(p) => (
          <Textarea {...p} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        )}
      </FormField>
      <div className={styles.file}>
        <input
          ref={file}
          type="file"
          accept="image/*,application/pdf"
          capture="environment"
          hidden
          aria-label={t('finance.receipt.choose')}
          onChange={(e) => {
            setUpload(e.target.files?.[0] ?? null);
            e.target.value = '';
          }}
        />
        <Button variant="secondary" size="sm" onClick={() => file.current?.click()}>
          {entry?.receipt ? t('finance.receipt.replace') : t('finance.receipt.attach')}
        </Button>
        {(upload ?? entry?.receipt) && (
          <Text size="sm" tone="muted">
            {upload ? upload.name : entry?.receipt?.filename}
          </Text>
        )}
      </div>
    </FormDialog>
  );
}
